import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Search,
    MoreHorizontal,
    ChevronsUpDown,
    ChevronLeft,
    ChevronRight,
    X,
    Copy,
    SquareArrowOutUpRight,
    Funnel,
    Pencil,
    Plus,
    CircleAlert,
    CircleX,
    ArrowRight,
    ChevronDown,
    Info
} from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import nolistingImage from '../../assets/img/brand/noImage.png'
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import Pagination from '../../components/Pagination';
import SortableHeader from '../../components/SortableHeader';
import SearchBox from '../../components/SearchBox';
import FilterDrawer from '../../components/FilterDrawer';
import { formatDateTime } from '../../utils/formateDate';
import ConfirmDeactivateModal from '../../components/ConfirmDeactivateModal';
import apiHelper from '../../apiHelper';
import AlertNotification from '../../components/AlertNotification';
import ConfirmArchiveModal from '../../components/ConfirmArchiveModal';
import SkuInfoDrawer from '../../components/SkuInfoDrawer';
import StockDrawer from '../../components/Stockdrawer';
import { useLoader } from '../../components/GlobalLoader';

// Helper function to determine platform status and colors based on last_sync_status
const getPlatformStatusInfo = (lastSyncStatus) => {
    if (!lastSyncStatus) {
        return {
            dotColor: '#CED4DA', // Light Gray
            textColor: '#212529', // Black text for platform name
            status: 'no_data'
        };
    }

    switch (lastSyncStatus.toLowerCase()) {
        case 'success':
        case 'completed':
        case 'synced':
            return {
                dotColor: '#146C43', // Green
                textColor: '#212529', // Black text for successful sync
                status: 'success'
            };
        case 'failed':
        case 'error':
            return {
                dotColor: '#B02A37', // Red
                textColor: '#58151C', // Red text for failed sync
                status: 'failed'
            };
        case 'pending':
        case 'processing':
            return {
                dotColor: '#CED4DA', // Grey for pending
                textColor: '#212529', // Black text for pending
                status: 'pending'
            };
        default:
            return {
                dotColor: '#CED4DA', // Gray
                textColor: '#CED4DA', // Gray text
                status: 'unknown'
            };
    }
};

const PAGE_SIZE = 20;

export default function ListingPricingTest() {
    // --- Breadcrumbs & React Router ---
    const breadcrumbs = ["Listing", "Platform Listing (Test - Prefetch)"];
    const history = useHistory();
    const location = useLocation();
    const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
    const [selectedStockId, setSelectedStockId] = useState(null);
    const [selectedListing, setSelectedListing] = useState(null);

    // --- Tab State ---
    const [activeTab, setActiveTab] = useState("listing");
    useEffect(() => {
        const incomingTab = location?.state?.activeTab;
        if (incomingTab) {
            setActiveTab(incomingTab);
            history.replace(location.pathname, {}); // clear state
        }
    }, [location.state, location.pathname, history]);

    // --- Search / Pagination / Selection State ---
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState([]);
    const [suppressHighlight, setSuppressHighlight] = useState(false);
    const filterButtonRef = useRef(null);
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [archivedListings, setArchivedListings] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState('entity-info');
    const drawerRef = useRef(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [tempFilter, setTempFilter] = useState({
        condition: [],
        status: [],
        baseMin: "",
        baseMax: "",
        platforms: [],
        erpMin: "",
        erpMax: "",
    });
    const [appliedFilter, setAppliedFilter] = useState(null);
    const BULK_PAGE_SIZE = 20;
    const [bulkActive, setBulkActive] = useState(false);
    const [bulkItems, setBulkItems] = useState([]);
    const [bulkPage, setBulkPage] = useState(1);
    const [bulkDropdownOpen, setBulkDropdownOpen] = useState(false);
    const bulkToolbarRef = useRef(null);
    const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
    const [toolbarDropdownOpen, setToolbarDropdownOpen] = useState(false);
    const toolbarRef = useRef(null);
    const [copied, setCopied] = useState(false);
    const [sortField, setSortField] = useState('modified_at');
    const [sortOrder, setSortOrder] = useState("desc");
    const [listings, setListings] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [refreshTick, setRefreshTick] = useState(0);

    // ── Prefetch ──────────────────────────────────────────────────────
    const prefetchCache = useRef(new Map());
    const prefetchInProgress = useRef(new Set());
    const isFirstSearchRender = useRef(true);
    const isFirstFilterRender = useRef(true);
    const tableRef = useRef();
    const [searchTermArchived, setSearchTermArchived] = useState("");
    const [currentPageArchived, setCurrentPageArchived] = useState(1);
    const [searchInputArchived, setSearchInputArchived] = useState(""); // typing
    const [selectedIdsArchived, setSelectedIdsArchived] = useState([]);
    const [suppressHighlightArchived, setSuppressHighlightArchived] = useState(false);
    const [selectedRowArchived, setSelectedRowArchived] = useState(null);
    const [drawerOpenArchived, setDrawerOpenArchived] = useState(false);
    const [drawerTabArchived, setDrawerTabArchived] = useState('entity-info');
    const [archivedModalOpen, setArchivedModalOpen] = useState(false);
    const dropdownRefArchived = useRef(null);
    const buttonRefsArchived = useRef({});
    const drawerRefArchived = useRef(null);
    const toolbarRefArchived = useRef(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [dropdownOpenIdArchived, setDropdownOpenIdArchived] = useState(null);
    const [toolbarDropdownOpenArchived, setToolbarDropdownOpenArchived] = useState(false);
    const [appliedFilterArchived, setAppliedFilterArchived] = useState(null);
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef(null);
    const buttonRefs = useRef({});
    const { showLoader, hideLoader } = useLoader();
    const [filters, setFilters] = useState({
        status: [],
        platforms: [],
        platformPrice_min: "",
        platformPrice_max: "",
        platformStock_min: "",
        platformStock_max: "",
    });

    const [tempValues, setTempValues] = useState({
        status: [],
        platforms: [],
        platformPrice_min: "",
        platformPrice_max: "",
        platformStock_min: "",
        platformStock_max: "",
    });
    const [skuDrawerOpen, setSkuDrawerOpen] = useState(false);
    const [selectedSkuRow, setSelectedSkuRow] = useState(null);
    const [stockDrawerId, setStockDrawerId] = useState(null);
    const [stockDrawerModelNumber, setStockDrawerModelNumber] = useState(null);
    const [spuDrawerOpen, setSpuDrawerOpen] = useState(false);
    const [selectedSpuRow, setSelectedSpuRow] = useState(null);

    // --- Helpers ---
    function parseCurrencyToNumber(str) {
        if (!str) return NaN;
        const m = String(str).replace(/[^\d.-]/g, "");
        return m === "" ? NaN : Number(m.replace(/,/g, ""));
    }

    function formatPrice(value) {
        const num = parseCurrencyToNumber(value);
        if (isNaN(num)) return '-';
        return num.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    // --- Condition Options ---
    const CONDITION_OPTIONS = [
        { key: "New|Unactivated", label: "New — Unactivated", bg: "#D1E7DD", border: "#198754", color: "#198754" },
        { key: "New|Activated", label: "New — Activated", bg: "#D1E7DD", border: "#198754", color: "#198754" },
        { key: "Used|Pristine", label: "Used — Pristine", bg: "#FFF3CD", border: "#997404", color: "#997404" },
        { key: "Used|Mint", label: "Used — Mint", bg: "#FFF3CD", border: "#997404", color: "#997404" },
        { key: "Used|Satisfactory", label: "Used — Satisfactory", bg: "#FFF3CD", border: "#997404", color: "#997404" },
    ];

    const mapListingApiToUi = (item, isArchived = false) => {
        const stock = item.stock || {};

        // Process platform data from the API
        // test_status comes from SalesListingMappingState; fall back to last_sync_status for older data
        const platforms = (item.platform_data || []).map(platform => {
            const syncStatus =  platform.last_sync_status;
            const statusInfo = getPlatformStatusInfo(syncStatus);
            const isFailed = syncStatus?.toLowerCase() === 'failed' ||
                syncStatus?.toLowerCase() === 'error';
            const isPending = !syncStatus ||
                syncStatus?.toLowerCase() === 'pending' ||
                syncStatus?.toLowerCase() === 'processing';

            return {
                name: platform.platform_name || platform.platform || 'Unknown Platform',
                platformProductId: platform.platform_product_id,
                platformSkuId: platform.platform_sku_id,
                platformPrice: platform.platform_price ? `${platform.platform_price}` : '-',
                platformStock: platform.platform_stock_quantity ?? '-',
                lastSyncTime: platform.last_sync_at ? formatDateTime(platform.last_sync_at) : '-',
                lastSyncStatus: syncStatus,
                lastErrorMessage: platform.last_error_message || platform.error_message,
                dotColor: statusInfo.dotColor,
                textColor: statusInfo.textColor,
                currencyCode: platform.platform_currency_code,
                isFailed: isFailed,
                isPending: isPending,
                productName: platform.platform_product_name,
                skuName: platform.platform_sku_name,
            };
        });

        const baseMapping = {
            id: item.sales_listing_id,
            name: stock.spu_name || "-",
            variant: stock.sku_name || "-",
            model_number: stock.model_number || "",
            avatar: stock.sku_image || nolistingImage,
            condition: stock.condition || "NA",
            condition_type: stock.condition_type || "NA",
            grading: stock.condition_type || "NA",
            stockStatus: stock.status ? "Active" : "Inactive",
            status: item.status ? "Active" : "Inactive",
            iD: item.listing_code || "",
            notes: item.notes || "",
            modified_at: item.modified_at || "",
            created_at: item.created_at || "",
            created_by: item.created_by || "",
            modified_by: item.modified_by || "",
            openId: item.open_id || "",
            stockId: item.stock_id || "",
            platforms: platforms,
            erpStock: stock.stock_status_summary?.inStock_Sellable?.find(
                (s) => s.type === "Sellable"
            )?.count ?? 0,
        };

        // Add archive fields only for archived listings
        if (isArchived) {
            return {
                ...baseMapping,
                archived_by: item.archived_by || "",
                archived_at: item.archived_at || "",
            };
        }

        return baseMapping;
    };

    // ── Archived fetch (one-time, no server-side pagination needed) ──
    const fetchListings = async (archived = false) => {
        const url = archived
            ? "sales/listings/?archived=true"
            : "sales/listings/";

        const response = await apiHelper(url, "GET");

        if (!response.ok) {
            throw new Error("Failed to fetch listings");
        }

        const data = await response.json();
        console.log("API Response:", data);
        const mappedListings = (Array.isArray(data) ? data : (data.results || [])).map(item => mapListingApiToUi(item, archived));

        if (archived) {
            setArchivedListings(mappedListings);
        }
    };

    const handleOpenSkuDrawer = (row) => {
        setSelectedSkuRow(row);
        setSkuDrawerOpen(true);
    };

    // ── One-time fetch for archived tab ───────────────────────────
    useEffect(() => {
        const loadArchived = async () => {
            try {
                await fetchListings(true);
            } catch (error) {
                console.error(error);
            }
        };
        loadArchived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    console.log("Listings state:", listings);
    console.log("Archived Listings state:", archivedListings);

    // ── Server-side helpers ───────────────────────────────────────────
    function buildListingCacheKey(page, search, af, sf, so) {
        return JSON.stringify({ page, search: (search || "").trim(), af, sf, so });
    }

    function buildListingOrdering(sf, so) {
        if (!sf) return '-modified_at';
        return `${so === 'desc' ? '-' : ''}${sf}`;
    }

    async function prefetchListingPage(page, search, af, sf, so, totalCnt) {
        const totalPgs = Math.ceil(totalCnt / PAGE_SIZE);
        if (page > totalPgs) return;
        const key = buildListingCacheKey(page, search, af, sf, so);
        if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
        prefetchInProgress.current.add(key);
        try {
            const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
            if (search && search.trim()) params.append('search', search.trim());
            if (af?.status?.length) params.append('status', af.status.join(','));
            if (af?.platforms?.length) params.append('platform', af.platforms.join(','));
            if (af?.condition?.length) params.append('condition', af.condition.join(','));
            params.append('ordering', buildListingOrdering(sf, so));
            const response = await apiHelper(`sales/listings/?${params.toString()}`, "GET");
            if (!response.ok) return;
            const json = await response.json();
            const results = (json.results || []).map(item => mapListingApiToUi(item, false));
            prefetchCache.current.set(key, { results, count: json.count || 0 });
        } catch (_) { /* silent */ } finally {
            prefetchInProgress.current.delete(key);
        }
    }

    // ── Reactive main fetch for active listings ───────────────────
    useEffect(() => {
        async function load() {
            const key = buildListingCacheKey(currentPage, debouncedSearch, appliedFilter, sortField, sortOrder);
            const cached = prefetchCache.current.get(key);
            if (cached) {
                setListings(cached.results);
                setTotalCount(cached.count);
                prefetchListingPage(currentPage + 1, debouncedSearch, appliedFilter, sortField, sortOrder, cached.count);
                return;
            }
            showLoader();
            try {
                const params = new URLSearchParams({ page: currentPage, page_size: PAGE_SIZE });
                if (debouncedSearch && debouncedSearch.trim()) params.append('search', debouncedSearch.trim());
                if (appliedFilter?.status?.length) params.append('status', appliedFilter.status.join(','));
                if (appliedFilter?.platforms?.length) params.append('platform', appliedFilter.platforms.join(','));
                if (appliedFilter?.condition?.length) params.append('condition', appliedFilter.condition.join(','));
                params.append('ordering', buildListingOrdering(sortField, sortOrder));
                const response = await apiHelper(`sales/listings/?${params.toString()}`, "GET");
                if (!response.ok) throw new Error("Failed to fetch listings");
                const json = await response.json();
                const mapped = (json.results || []).map(item => mapListingApiToUi(item, false));
                prefetchCache.current.set(key, { results: mapped, count: json.count || 0 });
                setListings(mapped);
                setTotalCount(json.count || 0);
                prefetchListingPage(currentPage + 1, debouncedSearch, appliedFilter, sortField, sortOrder, json.count || 0);
            } catch (error) {
                console.error(error);
            } finally {
                hideLoader();
            }
        }
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, debouncedSearch, appliedFilter, sortField, sortOrder, refreshTick]);

    // ── Debounce search input ─────────────────────────────────────────
    useEffect(() => {
        if (isFirstSearchRender.current) { isFirstSearchRender.current = false; return; }
        const t = setTimeout(() => {
            setCurrentPage(1);
            setDebouncedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // ── Clear cache on filter/sort change ────────────────────────────
    useEffect(() => {
        if (isFirstFilterRender.current) { isFirstFilterRender.current = false; return; }
        setCurrentPage(1);
        prefetchCache.current.clear();
        prefetchInProgress.current.clear();
    }, [appliedFilter, sortField, sortOrder]);

    // Server already handles filtering/sorting — listings is the current page
    const filtered = listings;

    const handleSort = (field) => {
        console.log("Sorting by:", field);
        if (sortField === field) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const pageData = listings;

    const handleDeactivateStock = async (listingId) => {
        if (!listingId) return;

        try {
            const payload = { status: false };

            const response = await apiHelper(
                `sales/listings/${listingId}/status/`,
                "PATCH",
                payload
            );

            if (!response.ok) {
                console.error("Failed to deactivate listing");
                return;
            }

            setSuccessMessage("Listing Deactivated Successfully");

            // ✅ close modal & cleanup
            setDeactivateModalOpen(false);
            setSelectedStockId(null);

            // Update status in local state then re-fetch from API
            // (same as EditListing: save → navigate → Platform Listing re-fetches)
            setSelectedRow((prev) => (prev ? { ...prev, status: 'Inactive' } : prev));
            prefetchCache.current.clear();
            prefetchInProgress.current.clear();
            setRefreshTick(t => t + 1);

        } catch (error) {
            console.error("Deactivate error:", error);
        }
    };

    // Activate: PATCH status true → backend cron triggers automatically (same as EditListing save).
    // Platform dot colors update gradually as the backend sync runs — no frontend action needed.
    const handleActivateListing = async (listing) => {
        if (!listing?.id) return;

        try {
            const response = await apiHelper(
                `sales/listings/${listing.id}/status/`,
                "PATCH",
                { status: true }
            );

            if (!response.ok) {
                console.error("Failed to activate listing");
                return;
            }

            setSuccessMessage("Listing Activated Successfully");
            setSelectedRow((prev) => (prev ? { ...prev, status: 'Active' } : prev));
            prefetchCache.current.clear();
            prefetchInProgress.current.clear();
            setRefreshTick(t => t + 1);

        } catch (error) {
            console.error("Activate error:", error);
        }
    };

    const clearSelection = () => {
        setSelectedIds([]);
        setBulkDropdownOpen(false);
        setBulkActive(false);
        setBulkItems([]);
    };

    // --- Bulk Pricing Handlers ---
    useEffect(() => {
        const pages = Math.max(1, Math.ceil(bulkItems.length / BULK_PAGE_SIZE));
        if (bulkPage > pages) setBulkPage(1);
    }, [bulkItems, bulkPage]);

    const handleAddToBulkPricing = () => {
        if (selectedIds.length === 0) return;
        setBulkActive(true);
        setBulkItems((prev) => {
            const alreadyInBulk = selectedIds.filter((id) => prev.includes(id));
            const newOnes = selectedIds.filter((id) => !prev.includes(id));
            if (alreadyInBulk.length > 0) {
                setShowDuplicateWarning(true);
                setTimeout(() => setShowDuplicateWarning(false), 4000);
            }
            return [...prev, ...newOnes];
        });
        setBulkDropdownOpen(true);
        setSelectedIds([]);
        const toolbarEl = bulkToolbarRef.current;
        if (toolbarEl) toolbarEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };

    const onRowClick = (row) => {
        setSelectedRow(row);
        setDrawerTab("entity-info");
        setDrawerOpen(true);
    };

    useEffect(() => {
        function handleClickOutsideDrawer(event) {
            // ERP-4940: outside click always closes the info drawer, even with a
            // nested drawer or confirmation modal open.
            if (drawerOpen && drawerRef.current && !drawerRef.current.contains(event.target)) {
                setDrawerOpen(false);
                setSelectedRow(null);
                setSelectedIds((ids) => (selectedRow ? ids.filter((id) => id !== selectedRow.id) : ids));
            }
        }
        document.addEventListener("mousedown", handleClickOutsideDrawer);
        return () => document.removeEventListener("mousedown", handleClickOutsideDrawer);
    }, [drawerOpen, selectedRow, deactivateModalOpen]);

    const toggleDropdown = (id) => {
        if (dropdownOpenId === id) {
            setDropdownOpenId(null);
            return;
        }
        const btn = buttonRefs.current[id];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            const dropdownWidth = 170;
            const dropdownHeight = 150;
            let left = rect.left + window.scrollX + rect.width - dropdownWidth;
            if (left < window.scrollX + 8) left = window.scrollX + 8;
            let top = rect.bottom + window.scrollY;
            if (top + dropdownHeight > window.scrollY + window.innerHeight) {
                top = rect.top + window.scrollY - dropdownHeight;
            }
            setDropdownPosition({ top, left });
        }
        setDropdownOpenId(id);
    };

    useEffect(() => {
        function handleClickOutside(e) {
            if (
                dropdownOpenId !== null &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target) &&
                !buttonRefs.current[dropdownOpenId]?.contains(e.target)
            ) {
                setDropdownOpenId(null);
            }
        }
        if (dropdownOpenId !== null) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownOpenId]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (bulkToolbarRef.current && !bulkToolbarRef.current.contains(e.target)) {
                setBulkDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleToolbarDropdown = () => setToolbarDropdownOpen((prev) => !prev);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
                setToolbarDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredArchived = useMemo(() => {
        const term = searchTermArchived.trim().toLowerCase();

        // ✅ define FIRST
        function matchesApplied(d) {
            if (appliedFilterArchived?.condition?.length) {
                const selectedKeys = appliedFilterArchived.condition;
                const rowKey = `${String(d.condition ?? "").trim()}|${String(d.grading ?? "").trim()}`;
                const usesCombinedKeys = selectedKeys.some(s => s.includes('|'));

                if (usesCombinedKeys) {
                    if (!selectedKeys.includes(rowKey)) return false;
                } else {
                    if (!selectedKeys.includes(String(d.condition ?? ""))) return false;
                }
            }

            if (appliedFilterArchived?.status?.length) {
                const selected = appliedFilterArchived.status.map(s => s.toLowerCase());
                if (!selected.includes(String(d.status ?? "").toLowerCase())) return false;
            }

            const baseNum = parseCurrencyToNumber(d.basePrice);
            if (appliedFilterArchived.baseMin && baseNum < Number(appliedFilterArchived.baseMin)) return false;
            if (appliedFilterArchived.baseMax && baseNum > Number(appliedFilterArchived.baseMax)) return false;

            if (appliedFilterArchived.platforms?.length) {
                const has = d.platforms?.some(p =>
                    appliedFilterArchived.platforms.includes(p.name)
                );
                if (!has) return false;
            }

            const erp = Number(d.erpStock ?? NaN);
            if (appliedFilterArchived.erpMin && erp < Number(appliedFilterArchived.erpMin)) return false;
            if (appliedFilterArchived.erpMax && erp > Number(appliedFilterArchived.erpMax)) return false;

            return true;
        }

        const searchFiltered = (archivedListings || []).filter(d => {
            return (
                term === "" ||
                String(d.name ?? '').toLowerCase().includes(term) ||
                String(d.variant ?? '').toLowerCase().includes(term) ||
                String(d.iD ?? '').toLowerCase().includes(term)
            );
        });

        const filtered = appliedFilterArchived
            ? searchFiltered.filter(matchesApplied)
            : searchFiltered;

        // 🔽 SORT
        if (!sortField) return filtered;

        return [...filtered].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal == null || bVal == null) return 0;

            if (sortField === "modified_at") {
                return sortOrder === "asc"
                    ? new Date(aVal) - new Date(bVal)
                    : new Date(bVal) - new Date(aVal);
            }

            if (typeof aVal === "string") {
                return sortOrder === "asc"
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }

            return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
        });

    }, [searchTermArchived, appliedFilterArchived, archivedListings, sortField, sortOrder]);

    const totalPagesArchived = Math.max(1, Math.ceil(filteredArchived.length / PAGE_SIZE));
    const pageDataArchived = filteredArchived.slice((currentPageArchived - 1) * PAGE_SIZE, currentPageArchived * PAGE_SIZE);

    const onRowClickArchived = (row) => {
        setSelectedRowArchived(row);
        setDrawerTabArchived('entity-info');
        setDrawerOpenArchived(true);
    };

    useEffect(() => {
        function handleClickOutsideDrawerArchived(event) {
            // ERP-4940: outside click always closes the info drawer, even with a
            // nested drawer or confirmation modal open.
            if (drawerOpenArchived && drawerRefArchived.current && !drawerRefArchived.current.contains(event.target)) {
                setDrawerOpenArchived(false);
                setSelectedRowArchived(null);
                setSelectedIdsArchived((ids) => (selectedRowArchived ? ids.filter((id) => id !== selectedRowArchived.id) : ids));
            }
        }
        document.addEventListener("mousedown", handleClickOutsideDrawerArchived);
        return () => document.removeEventListener("mousedown", handleClickOutsideDrawerArchived);
    }, [drawerOpenArchived, selectedRowArchived, archivedModalOpen]);

    useEffect(() => {
        function handleClickOutsideArchived(event) {
            if (
                dropdownOpenIdArchived !== null &&
                dropdownRefArchived.current &&
                !dropdownRefArchived.current.contains(event.target) &&
                !buttonRefsArchived.current[dropdownOpenIdArchived]?.contains(event.target)
            ) {
                setDropdownOpenIdArchived(null);
            }
        }
        if (dropdownOpenIdArchived !== null) {
            document.addEventListener("mousedown", handleClickOutsideArchived);
        }
        return () => document.removeEventListener("mousedown", handleClickOutsideArchived);
    }, [dropdownOpenIdArchived]);

    useEffect(() => {
        const handleClickOutsideToolbarArchived = (e) => {
            if (toolbarRefArchived.current && !toolbarRefArchived.current.contains(e.target)) {
                setToolbarDropdownOpenArchived(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutsideToolbarArchived);
        return () => document.removeEventListener("mousedown", handleClickOutsideToolbarArchived);
    }, []);



    //Archieve modal start here
    const archivedRef = useRef(null);
    const handleArchivedConfirm = () => {
        setArchivedModalOpen(false);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (archivedRef.current && !archivedRef.current.contains(event.target)) {
                setArchivedModalOpen(false);
            }
        }

        if (archivedModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [archivedModalOpen]);

    //Save Modal start here
    const saveRef = useRef(null);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const handleSaveConfirm = () => {
        setSaveModalOpen(false);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (saveRef.current && !saveRef.current.contains(event.target)) {
                setSaveModalOpen(false);
            }
        }

        if (saveModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [saveModalOpen]);

    const FilterActiveIcon = () => (
        <svg style={{ margin: 0 }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <g clipPath="url(#clip0_4516_4830)">
                <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
            </g>
            <defs>
                <clipPath id="clip0_4516_4830">
                    <rect width="12" height="12" fill="white" transform="translate(11 5)" />
                </clipPath>
            </defs>
        </svg>
    );

    const FilterInactiveIcon = () => (
        <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const FilterDisabledIcon = () => (
        <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#CED4DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#CED4DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const filtersApplied = Object.entries(filters || {}).some(([key, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== null && value !== "";
    });

    const isInitialTableEmpty = listings?.length === 0;
    const isInitialArchivedEmpty = archivedListings?.length === 0;

    const isSearching = searchTerm?.trim().length > 0;
    const isFilterApplied = filtersApplied;
    const isTableEmpty = isInitialTableEmpty && !isSearching && !isFilterApplied;

    const isArchivedSearching = searchTermArchived?.trim().length > 0;
    const isArchivedTableEmpty = isInitialArchivedEmpty && !isArchivedSearching;


    const platformOptions = [
        ...new Set(
            listings.flatMap(l => (l.platforms || []).map(p => p.name))
        )
    ].map(name => ({
        title: name,
        value: name
    }));

    const sections = [
        {
            label: "Status",
            type: "checkbox",
            key: "status",
            options: ["Active", "Inactive"],
            divider: true,
        },
        {
            label: "Platforms",
            type: "list",
            key: "platforms",
            data: platformOptions,
            renderItem: (item) => ({
                id: item.value,
                title: item.title,
                subtitle: "",
                image: null
            }),
            showDropdownIcon: true,
            optionHoverBg: '#F8F9FA',
            divider: true,
        },
        {
            label: "Platform Price",
            type: "range",
            key: "platformPrice",
            data: [],
            divider: true,
        },
        {
            label: "Platform Stock",
            type: "rangePlain",
            key: "platformStock",
            data: [],
        },
    ];
    const handleCopy = async (textToCopy) => {
        if (!textToCopy) {
            console.error('No text to copy');
            alert('Failed to copy: No text available.');
            return;
        }

        // Use Clipboard API if available and secure
        if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            try {
                await navigator.clipboard.writeText(textToCopy);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
            } catch (err) {
                console.error('Clipboard API failed:', err);
                fallbackCopyTextToClipboard(textToCopy);
            }
        } else {
            console.warn('Clipboard API not available or not in secure context, using fallback');
            fallbackCopyTextToClipboard(textToCopy);
        }
    };

    console.log("filters", filters);
    console.log("listing", filtered);

    const fallbackCopyTextToClipboard = (text) => {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } else {
                console.error('Fallback copy failed');
                alert('Failed to copy text. Please copy manually.');
            }
        } catch (err) {
            console.error('Fallback copy error:', err);
            alert('Failed to copy text. Please copy manually.');
        }
    };


    useEffect(() => {
        console.log('location.state on mount:', location.state);

        if (location && location.state && location.state.success) {
            setSuccessMessage(location.state.success);
            history.replace(location.pathname, {});

            const t = setTimeout(() => setSuccessMessage(''), 5000);
            return () => clearTimeout(t);
        }
    }, [location, history]);


    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(""), 5000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);
    useEffect(() => {
        console.log("APPLIED FILTER 👉", appliedFilter);
    }, [appliedFilter]);

    const handleToggleListingStatus = async (listingId, currentStatus) => {
        try {
            const payload = {
                status: currentStatus !== "Active",
            };

            const response = await apiHelper(
                `sales/listings/${listingId}/status/`,
                "PATCH",
                payload
            );

            if (!response.ok) {
                console.error("Failed to update listing status");
                return;
            }

            const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

            setSuccessMessage(
                currentStatus === "Active"
                    ? "Listing Deactivated Successfully"
                    : "Listing Activated Successfully"
            );

            setSelectedRow(prev => prev ? { ...prev, status: newStatus } : prev);
            prefetchCache.current.clear();
            prefetchInProgress.current.clear();
            setRefreshTick(t => t + 1);

        } catch (error) {
            console.error("Status update error:", error);
        }
    };
    const handleArchiveStock = async (listingId) => {
        if (!listingId) return;

        // 🔥 STEP 1: find listing locally
        const archivedItem = listings.find(l => l.id === listingId);
        if (!archivedItem) return;

        setListings(prev => prev.filter(l => l.id !== listingId));

        setArchivedListings(prev => [
            {
                ...archivedItem,
                status: "Inactive",   // optional
            },
            ...prev
        ]);

        try {
            const payload = { is_archived: true };

            const response = await apiHelper(
                `sales/listings/${listingId}/archive/`,
                "PATCH",
                payload
            );

            if (!response.ok) {
                throw new Error("Archive failed");
            }

            setSuccessMessage("Listing Archived Successfully");

        } catch (error) {
            console.error("Archive error:", error);

            // ❗ rollback if API fails
            setArchivedListings(prev => prev.filter(l => l.id !== listingId));
            setListings(prev => [...prev, archivedItem]);

            alert("Archive failed, please retry");
        } finally {
            setArchivedModalOpen(false);
            setSelectedStockId(null);
        }
    };

    return (
        <div className="company-body new-look stock-wrap bg-checkbox">
            <style>{`
                .listing-action-btn:focus,
                .listing-action-btn:active,
                .listing-action-btn:focus-visible {
                    outline: none !important;
                    box-shadow: none !important;
                }
                .listing-dropdown-btn:hover {
                    background: #FFFEF0 !important;
                }
                .listing-drawer-action-btn:focus,
                .listing-drawer-action-btn:active,
                .listing-drawer-action-btn:focus-visible {
                    outline: none !important;
                    box-shadow: none !important;
                }
                .listing-drawer-dropdown-btn:hover {
                    background: #FFFEF0 !important;
                }
            `}</style>
            {/* Breadcrumbs */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                    <span key={index} className="breadcrumb-item">
                        {crumb}
                        {index !== breadcrumbs.length - 1}
                    </span>
                ))}
            </div>

            {successMessage && (
                <AlertNotification
                    type={
                        successMessage.includes("Discarded") || successMessage.toLowerCase().includes("with error")
                            ? "warning"
                            : "success"
                    }
                    message={successMessage}
                    recordName="Listing"
                    action={
                        successMessage.includes("Archived")
                            ? "archive"
                            : successMessage.includes("Discarded")
                                ? "discard"
                                : successMessage.includes("Activated")
                                    ? "activate"
                                    : successMessage.includes("Deactivated")
                                        ? "deactivate"
                                        : "create"
                    }
                    onClose={() => setSuccessMessage("")}
                />
            )}

            <div className="employee-tabs-container">
                {/* ─── Tab Buttons ─── */}
                <div className="tabs">
                    <button className={`tab ${activeTab === 'listing' ? 'active' : ''}`}
                        onClick={() => setActiveTab('listing')}>
                        Listings
                    </button>
                    <button className={`tab ${activeTab === 'archived-listing' ? 'active' : ''}`}
                        onClick={() => setActiveTab('archived-listing')}>
                        Archived Listings
                    </button>
                </div>

                {/* ─── Tab Contents ─── */}
                {activeTab === 'listing' && (
                    <div className="tab-content" id="listing-content">
                        {/* Stock List Card */}
                        <div className="info-section">
                            <div className="info-card" style={{ overflow: 'unset' }}>
                                <div className="entities-tab">
                                    {/* Toolbar: Search + Filter on left; Edit/Plus on right */}
                                    <div className="toolbar">
                                        <div className="toolbar-left">
                                            <SearchBox
                                                searchInput={searchInput}
                                                setSearchInput={setSearchInput}
                                                setSearchTerm={setSearchTerm}
                                                setCurrentPage={setCurrentPage}
                                                disabled={isTableEmpty}
                                            />

                                            <div style={{ position: 'relative' }}>
                                                <button
                                                    ref={filterButtonRef}
                                                    className={`icon-btn p-2 filter-btn ${filtersApplied ? "applied" : ""}`}
                                                    onClick={() => !isTableEmpty && setFilterPopupOpen(!filterPopupOpen)}
                                                    disabled={isTableEmpty}
                                                    style={{
                                                        border: isTableEmpty
                                                            ? "1px solid var(--Gray-400, #CED4DA)"
                                                            : filtersApplied ? "1px solid #2C7BE5" : "1px solid #6C757D",
                                                        borderWidth: "1px",
                                                        color: isTableEmpty ? "#CED4DA" : filtersApplied ? "#2C7BE5" : "#6C757D",

                                                        background: isTableEmpty
                                                            ? "var(--Gray-100, #F8F9FA)" // disabled background
                                                            : filtersApplied
                                                                ? "#CFE2FF" // active filter
                                                                : "#FFFFFF", // normal

                                                        height: "40px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",

                                                        pointerEvents: isTableEmpty ? "none" : "auto", // block actions
                                                        cursor: isTableEmpty ? "not-allowed" : "pointer",
                                                        borderRadius: "8px",
                                                    }}
                                                >
                                                    {isTableEmpty ? (
                                                        <FilterDisabledIcon />
                                                    ) : filtersApplied ? (
                                                        <FilterActiveIcon />
                                                    ) : (
                                                        <FilterInactiveIcon />
                                                    )}
                                                </button>
                                                <FilterDrawer
                                                    isOpen={filterPopupOpen}
                                                    onClose={() => {
                                                        setTempValues({
                                                            status: [],
                                                            platforms: [],
                                                            platformPrice_min: "",
                                                            platformPrice_max: "",
                                                            platformStock_min: "",
                                                            platformStock_max: "",
                                                        });
                                                        setFilterPopupOpen(false);
                                                    }}

                                                    sections={sections}
                                                    tempValues={tempValues}
                                                    setTempValues={setTempValues}

                                                    onApply={() => {
                                                        setFilters({
                                                            status: tempValues.status || [],
                                                            platforms: tempValues.platforms || [],
                                                            platformPrice_min: tempValues.platformPrice_min || "",
                                                            platformPrice_max: tempValues.platformPrice_max || "",
                                                            platformStock_min: tempValues.platformStock_min || "",
                                                            platformStock_max: tempValues.platformStock_max || "",
                                                        });

                                                        setFilterPopupOpen(false);
                                                    }}

                                                    onClear={() => {

                                                        const resetValues = {
                                                            status: [],
                                                            platforms: [],
                                                            platformPrice_min: "",
                                                            platformPrice_max: "",
                                                            platformStock_min: "",
                                                            platformStock_max: "",
                                                        };

                                                        setTempValues(resetValues);
                                                        setFilters(resetValues);
                                                    }}
                                                    enableAutoSwap={true}
                                                />
                                            </div>
                                        </div>

                                        <div className="toolbar-right" ref={toolbarRef}>

                                            {/* Flow-2 End */}
                                            <button className="icon-btn dark-border"
                                                onClick={() => history.push("/listing-pricing/add-listing")}
                                            >
                                                <Plus size={18} /> Add Listing
                                            </button>
                                        </div>
                                    </div>

                                    {/* Table */}
                                    <div className="table-wrapper" style={{
                                        border: 'none'
                                    }}>
                                        {pageData.length === 0 && !debouncedSearch ? (
                                            <div className="empty-state">
                                                <div className="empty-state-content">
                                                    <div className="icon"><img
                                                        src={noBrandImage}
                                                        alt="avatar"
                                                        className="avatar"
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            verticalAlign: 'middle'
                                                        }}
                                                    /></div>
                                                    <h1 className="no-item-title mt-2">
                                                        No Listings Found
                                                    </h1>
                                                    <p className="no-item-text">
                                                        Nothing here yet. Add your first item to get started!
                                                    </p>
                                                </div>
                                            </div>
                                        ) : totalCount === 0 && debouncedSearch ? (
                                            <div className="empty-state">
                                                <div className="empty-state-content">
                                                    <div className="icon"><img
                                                        src={emptySearchImg}
                                                        alt="avatar"
                                                        className="avatar"
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            verticalAlign: 'middle'
                                                        }}
                                                    /></div>
                                                    <h1 className="no-item-title mt-2">
                                                        No Listings Found
                                                    </h1>
                                                    <p className="no-item-text">
                                                        No results match what you're looking for
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <table className="entities-table">
                                                <thead height="45px">
                                                    <tr>
                                                        <SortableHeader
                                                            label="Stock"
                                                            field="name"
                                                            sortField={sortField}
                                                            sortOrder={sortOrder}
                                                            handleSort={handleSort}
                                                            classes="sticky-action-col sticky-left"
                                                        />
                                                        <SortableHeader
                                                            label="Status"
                                                            field="status"
                                                            sortField={sortField}
                                                            sortOrder={sortOrder}
                                                            handleSort={handleSort}
                                                        />
                                                        <SortableHeader
                                                            label="ID"
                                                            field="iD"
                                                            sortField={sortField}
                                                            sortOrder={sortOrder}
                                                            handleSort={handleSort}
                                                        />

                                                        <th>
                                                            <span>Platforms</span>
                                                        </th>
                                                        <th>
                                                            <span>Platform Price</span>
                                                        </th>

                                                        <th>
                                                            <span>Platform Stock</span>
                                                        </th>

                                                        <th>
                                                            <span>Last Sync Time</span>
                                                        </th>
                                                        <SortableHeader
                                                            label="Last Modified Time"
                                                            field="modified_at"
                                                            sortField={sortField}
                                                            sortOrder={sortOrder}
                                                            handleSort={handleSort}
                                                        />
                                                        <th className="sticky-action-col" style={{ textAlign: "center" }}>
                                                            Action
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {pageData.map((row, idx) => {
                                                        const isRowSelected =
                                                            (selectedIds.includes(row.id) || dropdownOpenId === row.id) &&
                                                            !suppressHighlight;

                                                        return (
                                                            <tr
                                                                key={row.id}
                                                                className={[idx % 2 ? "striped" : "", isRowSelected ? "row-selected" : ""].join(" ")}
                                                                onClick={() => onRowClick(row)}
                                                            >
                                                                {/* Stock */}
                                                                <td className="sticky-action-col sticky-left">
                                                                    <div className="name-cell" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                                        <img
                                                                            src={row.avatar}
                                                                            alt="avatar"
                                                                            className="avatar"
                                                                            style={{
                                                                                width: 40,
                                                                                height: 40,
                                                                                marginRight: 8,
                                                                                verticalAlign: "middle"
                                                                            }}
                                                                        />
                                                                        <div>
                                                                            <div>{row.name}</div>
                                                                            <div className="sub-line mt-1">{row.variant}{row.model_number ? ` | ${row.model_number}` : ''}</div>
                                                                            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5 }}>
                                                                                <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00" }}>{row.condition}</span>
                                                                                <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00" }}>{row.grading}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {/* Status */}
                                                                <td>
                                                                    <span className={`status ${row.status?.toLowerCase() ?? ""}`}>{row.status}</span>
                                                                </td>

                                                                {/* ID - Show NA in #212529 */}
                                                                <td>
                                                                    {row.iD ? row.iD : <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                </td>

                                                                {/* Platforms - vertical aligned with dots only */}
                                                                <td>
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                                        {row.platforms && row.platforms.length > 0 ? (
                                                                            row.platforms.map((p, i) => (
                                                                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                                                                                        <circle cx="4" cy="4" r="3" fill={p.dotColor} />
                                                                                    </svg>
                                                                                    <span style={{ color: p.textColor }}>{p.name}</span>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            // Show "-" when no platform data
                                                                            <div style={{ color: '#6C757D', fontSize: 13 }}>-</div>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* Platform Price - Show only for success, otherwise "-" */}
                                                                <td>
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                                        {row.platforms && row.platforms.length > 0 ? (
                                                                            row.platforms.map((p, i) => (
                                                                                <div key={i} style={{ color: p.textColor, fontSize: 13 }}>
                                                                                    {p.isFailed || p.isPending || p.platformPrice == '-' ? '-' : `SGD ${formatPrice(p.platformPrice)}`}
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <div style={{ color: '#6C757D', fontSize: 13 }}>-</div>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* Platform Stock - Show only for success, otherwise "-" */}
                                                                <td>
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                                        {row.platforms && row.platforms.length > 0 ? (
                                                                            row.platforms.map((p, i) => (
                                                                                <div key={i} style={{ color: p.textColor, fontSize: 13 }}>
                                                                                    {p.isFailed || p.isPending ? '-' : p.platformStock}
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <div style={{ color: '#6C757D', fontSize: 13 }}>-</div>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* Last Sync Time - Show for success/failed, "-" for pending */}
                                                                <td>
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
                                                                        {row.platforms && row.platforms.length > 0 ? (
                                                                            row.platforms.map((p, i) => (
                                                                                <div key={i} style={{ color: p.textColor }}>
                                                                                    {p.isPending ? '-' : p.lastSyncTime}
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <div style={{ color: '#6C757D' }}>-</div>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
                                                                        {formatDateTime(row.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </td>

                                                                {/* Action */}
                                                                <td className="sticky-action-col" style={{ textAlign: "center" }}>
                                                                    <button
                                                                        ref={(el) => (buttonRefs.current[row.id] = el)}
                                                                        className="icon-btn listing-action-btn"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleDropdown(row.id);
                                                                            setDropdownOpenId(row.id);
                                                                            setSelectedRow(row);
                                                                        }}
                                                                    >
                                                                        <MoreHorizontal size={20} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        )}
                                        {/* Per-row dropdown menu */}
                                        {dropdownOpenId !== null && selectedRow && (
                                            <div
                                                ref={dropdownRef}
                                                className="backdrop-cls"
                                                style={{
                                                    position: "absolute",
                                                    top: dropdownPosition.top,
                                                    left: dropdownPosition.left,
                                                    background: "white",
                                                    boxShadow: "0px 1px 4px 0px #0000001A",
                                                    zIndex: 1000,
                                                    minWidth: 152,
                                                    borderRadius: 8,
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <div className="btn-align-forall">
                                                    <div className="common-for-all">
                                                        <button
                                                            className="listing-dropdown-btn"
                                                            onClick={() => {
                                                                onRowClick(selectedRow);
                                                                setDropdownOpenId(null);
                                                            }}
                                                        >
                                                            View Details
                                                        </button>
                                                    </div>

                                                    <div className="common-for-all">
                                                        <button
                                                            className="listing-dropdown-btn"
                                                            onClick={() => {
                                                                console.log("Sending row to edit:", selectedRow);
                                                                history.push("/listing-pricing/edit-listing", {
                                                                    listing: selectedRow
                                                                });
                                                            }}
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>

                                                    {/* Active / Deactivate */}
                                                    <div className="common-for-all">
                                                        {selectedRow?.status === "Active" ? (
                                                            <button
                                                                className="listing-dropdown-btn"
                                                                onClick={() => {
                                                                    setSelectedListing(selectedRow);
                                                                    setDeactivateModalOpen(true);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                Deactivate
                                                            </button>
                                                        ) : (
                                                            <button
                                                                className="listing-dropdown-btn"
                                                                onClick={() => {
                                                                    handleActivateListing(selectedRow);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                Activate
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="common-for-all">
                                                        <button
                                                            className="listing-dropdown-btn deactivate-cls"
                                                            onClick={() => {
                                                                setSelectedStockId(selectedRow?.id);
                                                                setArchivedModalOpen(true);
                                                                setDropdownOpenId(null);
                                                            }}
                                                        >
                                                            Archive
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {showDuplicateWarning && (
                                            <div
                                                className="d-flex align-items-center justify-content-between"
                                                role="alert"
                                                style={{
                                                    boxShadow: '0px 4px 10px 0px #0000001F',
                                                    padding: '15px 10px',
                                                    borderRadius: '12px',
                                                    border: '1px solid #FFC107',
                                                    backgroundColor: '#FFF3CD',
                                                    color: '#664D03',
                                                    fontSize: '12px',
                                                    position: 'fixed',
                                                    top: '10px',
                                                    width: 'calc(100% - 20px)',
                                                    maxWidth: '610px',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    zIndex: 2000
                                                }}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <Info className="me-2" />
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <strong>Duplicate entries weren’t added</strong> —— All other items have been successfully added
                                                    </div>
                                                </div>

                                                <X
                                                    size={16}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setShowDuplicateWarning(false)}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Pagination */}
                                    {totalCount > 0 && (
                                        <div className="pagination" style={{ marginTop: 16 }}>
                                            <div className="pagination-info">
                                                {`${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} `}
                                            </div>
                                            <Pagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                onPageChange={setCurrentPage}
                                            />
                                        </div>
                                    )}

                                    {selectedIds.length > 0 && (
                                        <div className="bottom-selection-bar">
                                            <div className="bulk-count-bubble">
                                                <div>{selectedIds.length} Selected</div>
                                                <button onClick={clearSelection} title="Clear selection">
                                                    <X size={18} />
                                                </button>
                                            </div>

                                            <div>
                                                <button
                                                    onClick={handleAddToBulkPricing}
                                                    className="bulk-action-btn"
                                                >
                                                    <Plus size={18} /> Add to Bulk Pricing
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* ------------------ Filter Drawer (RIGHT) ------------------ */}
                                    {filterOpen && (
                                        <>
                                            <div
                                                className="drawer-overlay"
                                                onClick={() => {
                                                    // clicking backdrop should cancel (no apply)
                                                    setFilterOpen(false);
                                                }}
                                            />

                                            <div
                                                className="entity-drawer"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ maxWidth: "400px" }}
                                            >
                                                <div className="drawer-header">
                                                    <h3>Filter</h3>
                                                    <button
                                                        className="close-btn"
                                                        onClick={() => { setFilterOpen(false); }}
                                                    >
                                                        <X size={24} />
                                                    </button>
                                                </div>
                                                <div className="main-drawer-inner">
                                                    <div className="drawer-content">
                                                        <div className="tab-panel">
                                                            {/* Condition */}
                                                            <div className="field-block pt-0">
                                                                <div className='field-label' style={{ fontSize: 14, fontWeight: '600' }}>Condition</div>
                                                                {CONDITION_OPTIONS.map(opt => (
                                                                    <label key={opt.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: 10 }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            style={{ margin: 0 }}
                                                                            checked={(tempFilter.condition || []).includes(opt.key)}
                                                                            onChange={(e) => {
                                                                                const checked = e.target.checked;
                                                                                setTempFilter(prev => {
                                                                                    const setc = new Set(prev.condition || []);
                                                                                    if (checked) setc.add(opt.key); else setc.delete(opt.key);
                                                                                    return { ...prev, condition: Array.from(setc) };
                                                                                });
                                                                            }}
                                                                        />

                                                                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                                            <span style={{
                                                                                background: opt.bg,
                                                                                border: `1px solid ${opt.border}`,
                                                                                borderRadius: 99,
                                                                                padding: "2px 10px",
                                                                                fontSize: 12,
                                                                                fontWeight: 600,
                                                                                color: opt.color,
                                                                                lineHeight: "normal"
                                                                            }}>{opt.label.split(' — ')[0]}</span>

                                                                            <div style={{ fontSize: 14, fontWeight: 400, color: "#212529" }}>
                                                                                {opt.label.split(' — ')[1]}
                                                                            </div>
                                                                        </div>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                            {/* Status */}
                                                            <div className="field-block">
                                                                <div className='field-label' style={{ fontSize: 14, fontWeight: '600' }}>Status</div>
                                                                {["Active", "Inactive"].map(s => (
                                                                    <label key={s} style={{ display: "flex", alignItems: "center", gap: 8, padding: 10 }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            style={{ margin: 0 }}
                                                                            checked={tempFilter.status.includes(s)}
                                                                            onChange={e => {
                                                                                setTempFilter(prev => {
                                                                                    const set = new Set(prev.status);
                                                                                    if (e.target.checked) set.add(s);
                                                                                    else set.delete(s);
                                                                                    return { ...prev, status: [...set] };
                                                                                });
                                                                            }}
                                                                        />
                                                                        <div style={{ fontSize: 14, fontWeight: 400, color: "#212529" }}>
                                                                            {s}
                                                                        </div>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                            {/* Base Price */}
                                                            <div className="field-block">
                                                                <div className='field-label' style={{ fontSize: 14, fontWeight: '600' }}>Base Price</div>
                                                                <div style={{ display: "flex", gap: 10 }} className="main-company-body-inner">
                                                                    <div>
                                                                        <label style={{ fontSize: 12, fontWeight: 400, color: "#212529" }}>Min</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control common-input-all"
                                                                            placeholder="Min"
                                                                            value={tempFilter.baseMin}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                setTempFilter(prev => ({ ...prev, baseMin: val }));
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label style={{ fontSize: 12, fontWeight: 400, color: "#212529" }}>Max</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control common-input-all"
                                                                            placeholder="Max"
                                                                            value={tempFilter.baseMax}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                setTempFilter(prev => ({ ...prev, baseMax: val }));
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {/* Platforms */}
                                                            <div className="field-block">
                                                                <div className='field-label' style={{ fontSize: 14, fontWeight: '600' }}>Platforms</div>
                                                                {["Shopee SG", "Lazada SG", "TikTok SG"].map(pl => (
                                                                    <label key={pl} style={{ display: "flex", alignItems: "center", gap: 8, padding: 10 }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            style={{ margin: 0 }}
                                                                            checked={tempFilter.platforms.includes(pl)}
                                                                            onChange={(e) => {
                                                                                setTempFilter(prev => {
                                                                                    const setp = new Set(prev.platforms || []);
                                                                                    if (e.target.checked) setp.add(pl); else setp.delete(pl);
                                                                                    return { ...prev, platforms: Array.from(setp) };
                                                                                });
                                                                            }}
                                                                        />
                                                                        <div style={{ fontSize: 14, fontWeight: 400, color: "#212529" }}>
                                                                            {pl}
                                                                        </div>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                            {/* ERP Stock */}
                                                            <div className="field-block" style={{ borderBottom: 0, paddingBottom: 50 }}>
                                                                <div className='field-label' style={{ fontSize: 14, fontWeight: '600' }}>ERP Stock</div>
                                                                <div style={{ display: "flex", gap: 10 }} className="main-company-body-inner">
                                                                    {/* ERP Stock Min */}
                                                                    <div>
                                                                        <label style={{ fontSize: 12, fontWeight: 400, color: "#212529" }}>Min</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control common-input-all"
                                                                            placeholder="Min"
                                                                            value={tempFilter.erpMin}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;            // <- capture value immediately
                                                                                setTempFilter(prev => ({ ...prev, erpMin: val }));
                                                                            }}
                                                                        />
                                                                    </div>

                                                                    {/* ERP Stock Max */}
                                                                    <div>
                                                                        <label style={{ fontSize: 12, fontWeight: 400, color: "#212529" }}>Min</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control common-input-all"
                                                                            placeholder="Max"
                                                                            value={tempFilter.erpMax}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;            // <- capture value immediately
                                                                                setTempFilter(prev => ({ ...prev, erpMax: val }));
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {/* Actions */}
                                                            <div className="field-block">
                                                                <div className='toolbar' style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", gap: 10, marginTop: "auto" }}>
                                                                    <button
                                                                        className="icon-btn"
                                                                        style={{ minWidth: 100 }}
                                                                        onClick={() => {
                                                                            // cancel -> restore temp to applied and close
                                                                            setTempFilter(appliedFilter || {
                                                                                condition: [],
                                                                                status: "",
                                                                                baseMin: "",
                                                                                baseMax: "",
                                                                                platforms: [],
                                                                                erpMin: "",
                                                                                erpMax: ""
                                                                            });
                                                                            setFilterOpen(false);
                                                                        }}
                                                                    >
                                                                        Cancel
                                                                    </button>

                                                                    <button
                                                                        className="icon-btn dark-border"
                                                                        style={{ minWidth: 100 }}
                                                                        onClick={() => {
                                                                            // apply filter (if all empty we clear filters)
                                                                            const hasAny = (
                                                                                (Array.isArray(tempFilter.condition) && tempFilter.condition.length > 0) ||
                                                                                (Array.isArray(tempFilter.status) && tempFilter.status.length > 0) ||
                                                                                Boolean(tempFilter.baseMin) ||
                                                                                Boolean(tempFilter.baseMax) ||
                                                                                (Array.isArray(tempFilter.platforms) && tempFilter.platforms.length > 0) ||
                                                                                Boolean(tempFilter.erpMin) ||
                                                                                Boolean(tempFilter.erpMax)
                                                                            );

                                                                            setAppliedFilter(hasAny ? { ...tempFilter } : null);
                                                                            setFilterOpen(false);
                                                                            setCurrentPage(1);
                                                                        }}
                                                                    >
                                                                        Filter
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {/* ------------------ End Filter Drawer ------------------ */}

                                    {/* Flow-2 End */}
                                    {/* )} */}
                                    {saveModalOpen && (
                                        <>
                                            <div className="drawer-overlay" />
                                            <div
                                                className="entity-drawer modal-popup-cls"
                                                ref={saveRef}
                                                style={{
                                                    maxWidth: 650,
                                                    margin: "0 auto",
                                                    background: "white",
                                                    borderRadius: 12,
                                                    zIndex: 1050,
                                                    padding: 24,
                                                    position: "fixed",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                                    height: "auto",
                                                    right: "auto",
                                                    overflow: "inherit",
                                                }}
                                            >
                                                <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                                    <button className="close-btn" onClick={() => setSaveModalOpen(false)}>
                                                        <X size={20} />
                                                    </button>
                                                </div>
                                                <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>New feature in Progress</h3>
                                                <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>Work in Progress</p>
                                                <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                                    <button className="btn-save" onClick={handleSaveConfirm}>
                                                        OK
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Single‐row Drawer */}
                        {drawerOpen && selectedRow && (() => {
                            const pendingCount = selectedRow.pending?.reduce((sum, item) => sum + item.count, 0) || 0;
                            const total = Number(selectedRow.available || 0) + pendingCount;
                            return (
                                <>
                                    <div className="drawer-overlay" />
                                    <div className="entity-drawer" ref={drawerRef}>
                                        <div className="drawer-header" style={{ background: "#F8F9FA" }}>
                                            <h3>Listing Info</h3>
                                            <button
                                                className="close-btn"
                                                onClick={() => {
                                                    setDrawerOpen(false);
                                                    setSelectedIds(ids => ids.filter(id => id !== selectedRow?.id));
                                                    setSelectedRow(null);
                                                }}
                                            >
                                                <X size={24} />
                                            </button>
                                        </div>
                                        <div className="main-drawer-inner" >
                                            <div className="drawer-title d-flex align-items-center justify-content-between" style={{ borderBottom: "none", paddingBottom: "0" }}>
                                                <div className="name-cell" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <img
                                                        src={selectedRow.avatar}
                                                        alt="avatar"
                                                        className="avatar"
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            marginRight: 8,
                                                            verticalAlign: "middle"
                                                        }}
                                                    />
                                                    <div>
                                                        <div style={{ color: "#212529", fontStyle: "normal", fontWeight: "400", lineHeight: "normal", fontSize: '12px' }}>{selectedRow.name}</div>
                                                        <div style={{ color: "#6C757D", fontSize: "10px", fontStyle: "normal", fontWeight: "400", lineHeight: "normal" }} className="sub-line mt-1">{selectedRow.variant}{selectedRow.model_number ? ` | ${selectedRow.model_number}` : ''}</div>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5 }}>
                                                            <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00", borderRadius: "99px", color: "#212529", fontWeight: "600" }}>{selectedRow.condition}</span>
                                                            <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00", borderRadius: "99px", color: "#212529", fontWeight: "600" }}>{selectedRow.grading}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='entities-tab flex-row align-items-center gap-3'>
                                                    <div className="edit-organisation-info d-flex justify-content-end align-items-center gap-2 p-0">
                                                        <button className="dark-border gray-btn" type="button" style={{ background: "#212529", height: "34px", minWidth: "100px", justifyContent: "center", alignItems: "center", gap: "10px", border: "none" }}
                                                            onClick={() => {
                                                                console.log("Sending row to edit:", selectedRow);
                                                                history.push("/listing-pricing/edit-listing", {
                                                                    listing: selectedRow
                                                                });
                                                            }}
                                                        >Edit</button>
                                                    </div>
                                                    <div className='toolbar'>
                                                        <div className="toolbar-right" style={{ position: "relative" }} ref={toolbarRef}>
                                                            <button
                                                                className="drawer-dots listing-drawer-action-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleToolbarDropdown();
                                                                }}
                                                            >
                                                                <MoreHorizontal size={18} />
                                                            </button>

                                                            {toolbarDropdownOpen && (
                                                                <div
                                                                    className="backdrop-cls"
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: 40,
                                                                        right: 0,
                                                                        background: "white",
                                                                        boxShadow: "0px 1px 4px 0px #0000001A",
                                                                        zIndex: 1000,
                                                                        minWidth: 152,
                                                                        borderRadius: 8,
                                                                    }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div className="btn-align-forall">
                                                                        <div className="btn-align-forall">
                                                                            <div className="common-for-all">
                                                                                {selectedRow.status === "Active" ? (
                                                                                    <button
                                                                                        className="listing-drawer-dropdown-btn"
                                                                                        onClick={() => {
                                                                                            setSelectedListing(selectedRow);
                                                                                            setDeactivateModalOpen(true);
                                                                                            setDropdownOpenId(null);
                                                                                        }}
                                                                                    >
                                                                                        Deactivate
                                                                                    </button>
                                                                                ) : (
                                                                                    <button
                                                                                        className="listing-drawer-dropdown-btn"
                                                                                        onClick={() => {
                                                                                            handleActivateListing(selectedRow);
                                                                                            setDropdownOpenId(null);
                                                                                            setToolbarDropdownOpen(false);
                                                                                        }}
                                                                                    >
                                                                                        Activate
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        <div className="common-for-all">
                                                                            <button
                                                                                className="listing-drawer-dropdown-btn deactivate-cls"
                                                                                onClick={() => {
                                                                                    setSelectedStockId(selectedRow?.id);
                                                                                    setArchivedModalOpen(true);
                                                                                    setDropdownOpenId(null);
                                                                                }}>
                                                                                Archive
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="drawer-tabs">
                                                <button
                                                    className={drawerTab === 'entity-info' ? 'active' : ''}
                                                    onClick={() => setDrawerTab('entity-info')}
                                                >
                                                    General
                                                </button>
                                                <button
                                                    className={drawerTab === 'platform' ? 'active' : ''}
                                                    onClick={() => setDrawerTab('platform')}
                                                >
                                                    Platform
                                                </button>

                                                <button
                                                    className={drawerTab === 'additional-info' ? 'active' : ''}
                                                    onClick={() => setDrawerTab('additional-info')}
                                                >
                                                    Additional
                                                </button>
                                            </div>

                                            <div className="drawer-content">
                                                {drawerTab === 'entity-info' && (
                                                    <div className="tab-panel general-panel">
                                                        <div
                                                            className="card-section"
                                                            style={{
                                                                borderRadius: '12px',
                                                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                                overflow: 'hidden',
                                                                marginBottom: '20px',
                                                                marginTop: '15px',
                                                            }}
                                                        >
                                                            <div
                                                                className="card-header"
                                                                style={{
                                                                    backgroundColor: ' #F8F9FA',
                                                                    padding: '15px 20px',
                                                                    fontFamily: 'Montserrat',
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    color: ' #212529',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    alignSelf: 'stretch',
                                                                    borderRadius: '12px 12px 0px 0px',
                                                                    gap: '10px',
                                                                    height: '50px',
                                                                    borderBottom: 'none',
                                                                }}
                                                            >
                                                                Basic Info
                                                            </div>
                                                            {/* BODY */}
                                                            <div
                                                                className="card-body"
                                                                style={{
                                                                    backgroundColor: '#FFFFFF',
                                                                    padding: '16px',
                                                                    display: 'grid',
                                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                                    gap: '16px',
                                                                }}
                                                            >
                                                                <div className="field-block" style={{
                                                                    borderBottom: 'none',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    minHeight: '60px',
                                                                    padding: '0'
                                                                }} >
                                                                    <div className="field-label">Listing Stock</div>
                                                                    <div className="name-cell" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                                        <img
                                                                            src={selectedRow.avatar}
                                                                            alt="avatar"
                                                                            className="avatar"
                                                                            style={{
                                                                                width: 32,
                                                                                height: 32,
                                                                                marginRight: 8,
                                                                                verticalAlign: "middle"
                                                                            }}
                                                                        />
                                                                        <div>
                                                                            <a
                                                                                href="#"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    console.log("Clicked SPU/SKU link for row:", selectedRow);
                                                                                    console.log("Clicked SPU/SKU link for row:", selectedRow);

                                                                                    // if (selectedRow?.stockId) {
                                                                                    //     history.push(
                                                                                    //         `/stock-list?id=${selectedRow.stockId}&autoOpen=true&listingId=${selectedRow.id}&spuTab=${drawerTab}`
                                                                                    //     );
                                                                                    // }
                                                                                    // else {
                                                                                    //     console.log("No stockId available for this listing, cannot navigate to stock details.");
                                                                                    // }
                                                                                    e.stopPropagation();
                                                                                    // handleOpenSkuDrawer(selectedRow);
                                                                                    if (selectedRow?.stockId) {
                                                                                        setStockDrawerId(selectedRow.stockId);
                                                                        setStockDrawerModelNumber(selectedRow.model_number || null);
                                                                                    } else {
                                                                                        console.log("No stockId available");
                                                                                        alert("No stock information available for this listing.");
                                                                                    }
                                                                                }}
                                                                                rel="noopener noreferrer"
                                                                                className="d-flex align-items-center gap-1 me-3"
                                                                                style={{ color: '#2C7BE5', textDecoration: 'underline', fontWeight: '600', fontSize: '12px' }}
                                                                            >
                                                                                {selectedRow.name}
                                                                                <SquareArrowOutUpRight size={14} strokeWidth={3} />
                                                                            </a>
                                                                            <div style={{ color: "#6C757D", fontSize: "10px", fontStyle: "normal", fontWeight: "400", lineHeight: "normal" }} className="sub-line mt-1">{selectedRow.variant}{selectedRow.model_number ? ` | ${selectedRow.model_number}` : ''}</div>
                                                                            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5 }}>
                                                                                <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00", borderRadius: "99px", color: "#212529", fontWeight: "600" }}>{selectedRow.condition}</span>
                                                                                <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00", borderRadius: "99px", color: "#212529", fontWeight: "600" }}>{selectedRow.grading}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="field-block" style={{
                                                                    borderBottom: 'none',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    minHeight: '60px',
                                                                    padding: '0'
                                                                }}>
                                                                    <div className="field-label">Status</div>
                                                                    <div className="field-value">
                                                                        <span className={`status ${String(selectedRow?.status || '').toLowerCase()}`}>
                                                                            {selectedRow?.status || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="field-block"
                                                                    style={{
                                                                        borderBottom: 'none',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'flex-start',
                                                                        padding: '0'
                                                                    }}>
                                                                    <div className="field-label" style={{ marginBottom: '4px' }}>ID</div>
                                                                    <div className="field-value" style={{ width: '100%', wordBreak: 'break-all' }}>
                                                                        {selectedRow.iD || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="field-block"
                                                                    style={{
                                                                        borderBottom: 'none',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'flex-start',
                                                                        padding: '0',
                                                                    }}
                                                                >
                                                                    <div className="field-label" style={{ marginBottom: '4px' }}>
                                                                        Open ID
                                                                    </div>

                                                                    <div
                                                                        className="field-value"
                                                                        style={{ width: '100%', wordBreak: 'break-all', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                                    >
                                                                        {selectedRow.openId ? (
                                                                            <div className="field-value d-flex align-items-center gap-2">
                                                                                <div>
                                                                                    <span
                                                                                    style={{
                                                                                        color: '#0D6EFD',
                                                                                        cursor: 'pointer',
                                                                                        fontWeight: '500',
                                                                                        textDecoration: 'underline',
                                                                                    }}
                                                                                    onClick={() => handleCopy(selectedRow.openId)}>

                                                                                    {selectedRow.openId}
                                                                                    </span>
                                                                                    <Copy
                                                                                        size={13}
                                                                                        onClick={() => handleCopy(selectedRow.openId)}
                                                                                        style={{ cursor: 'pointer', color: '#0D6EFD', marginLeft: '6px', }}
                                                                                    />
                                                                                    {copied && (
                                                                                        <span style={{ fontSize: '12px', color: '#0D6EFD', marginLeft: '6px', textDecoration: 'none',lineHeight:'normal',fontWeight: 500}}>
                                                                                            Copied!
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <span style={{ color: '#ADB5BD' }}>NA</span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0' }}>
                                                                    <div className="field-label">Created By</div>
                                                                    <div className="field-value">{selectedRow.created_by || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                                </div>

                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', }}>
                                                                    <div className="field-label">Created Time</div>
                                                                    <div className="field-value">
                                                                        {formatDateTime(selectedRow.created_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>

                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', }}>
                                                                    <div className="field-label">Last Modified By</div>
                                                                    <div className="field-value">
                                                                        {selectedRow.modified_by || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>

                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', }}>
                                                                    <div className="field-label">Last Modified Time</div>
                                                                    <div className="field-value">
                                                                        {formatDateTime(selectedRow.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {stockDrawerId && (
                                                    <StockDrawer
                                                        stockId={stockDrawerId}
                                                        setDrawerOpen={() => setStockDrawerId(null)}
                                                        setSkuDrawerOpen={setSkuDrawerOpen}
                                                        setSelectedRow={setSelectedSkuRow}
                                                        fallbackModelNumber={stockDrawerModelNumber}
                                                        skuDrawerOpen={skuDrawerOpen}
                                                    />
                                                )}
                                                {skuDrawerOpen && (
                                                    <SkuInfoDrawer
                                                        selectedRow={selectedSkuRow}
                                                        setSelectedRow={setSelectedSkuRow}
                                                        setSkuDrawerOpen={setSkuDrawerOpen}
                                                    />
                                                )}
                                                {drawerTab === 'platform' && (
                                                    <div className="tab-panel platform-panel">
                                                        <div className='entities-tab' style={{ padding: 0, paddingTop: '20px' }}>
                                                            <div className='table-wrapper'>
                                                                <table className="entities-table" ref={tableRef}>
                                                                    <thead height="45px">
                                                                        <tr>
                                                                            <th style={{
                                                                                position: "sticky",
                                                                                left: 0,
                                                                                zIndex: 3,
                                                                            }}><span>Platform</span></th>
                                                                            <th><span>Product Name</span></th>
                                                                            <th><span>SKU Name</span></th>
                                                                            <th><span>Platform Price</span></th>
                                                                            <th><span>Platform Stock</span></th>
                                                                            <th><span>Last Sync Time</span></th>
                                                                            {/* <th><span>Status</span></th> */}
                                                                        </tr>
                                                                    </thead>

                                                                    <tbody>
                                                                        {selectedRow?.platforms && selectedRow.platforms.length > 0 ? (
                                                                            selectedRow.platforms.map((item, idx) => {
                                                                                const status = item.lastSyncStatus?.toLowerCase();
                                                                                console.log("Platform item status:", status);
                                                                                const hasStatus = status && status !== "";
                                                                                const isFailed = status === 'failed' || status === 'error';
                                                                                const isPending = status === 'pending' || status === 'processing';
                                                                                const isSuccess = status === 'success';
                                                                                
                                                                                return (
                                                                                    <tr key={idx} className={idx % 2 === 1 ? 'striped' : ''}>
                                                                                        <td style={{
                                                                                            position: "sticky",
                                                                                            left: 0,
                                                                                            zIndex: 2, background: "#fff", color: item.textColor, whiteSpace: "nowrap"
                                                                                        }}>
                                                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                                                                <span style={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                                {hasStatus && !isPending && (
                                                                                                isFailed ? (
                                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                                                        <path d="M7.96883 1.3335C4.31016 1.3335 1.3335 4.32416 1.3335 8.00016C1.3335 11.6762 4.32416 14.6668 8.00016 14.6668C11.6762 14.6668 14.6668 11.6762 14.6668 8.00016C14.6668 4.32416 11.6622 1.3335 7.96883 1.3335ZM8.66683 10.6668C8.66683 11.035 8.36835 11.3335 8.00016 11.3335C7.63197 11.3335 7.3335 11.035 7.3335 10.6668C7.3335 10.2986 7.63197 10.0002 8.00016 10.0002C8.36835 10.0002 8.66683 10.2986 8.66683 10.6668ZM8.66683 8.00016C8.66683 8.36835 8.36835 8.66683 8.00016 8.66683C7.63197 8.66683 7.3335 8.36835 7.3335 8.00016V5.3335C7.3335 4.96531 7.63197 4.66683 8.00016 4.66683C8.36835 4.66683 8.66683 4.96531 8.66683 5.3335V8.00016Z" fill="#B02A37" />
                                                                                                    </svg>
                                                                                                ) : isSuccess ? (
                                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                                                        <path d="M8.00016 1.3335C4.32416 1.3335 1.3335 4.32416 1.3335 8.00016C1.3335 11.6762 4.32416 14.6668 8.00016 14.6668C11.6762 14.6668 14.6668 11.6762 14.6668 8.00016C14.6668 4.32416 11.6762 1.3335 8.00016 1.3335ZM7.1168 10.4929C6.86858 10.7411 6.4662 10.7413 6.21771 10.4933L4.6638 8.94278C4.4033 8.68284 4.40277 8.26096 4.66262 8.00037C4.92264 7.73962 5.34484 7.73914 5.60545 7.99929L6.66616 9.05816L9.72416 6.00016C9.98447 5.73985 10.4065 5.73985 10.6668 6.00016C10.9271 6.26047 10.9271 6.68252 10.6668 6.94283L7.1168 10.4929Z" fill="#146C43" />
                                                                                                    </svg>
                                                                                                ): null
                                                                                                )}
                                                                                                </span>
                                                                                                <span style={{ color: item.textColor }}>{item.name}</span>
                                                                                            </span>
                                                                                        </td>
                                                                                        <td style={{
                                                                                            color: item.textColor, maxWidth: "420px",
                                                                                            whiteSpace: "nowrap",
                                                                                            overflow: "hidden",
                                                                                            textOverflow: "ellipsis"
                                                                                        }}>{ (item.productName && item.productName !== 'Invalid ID') ? item.productName : '-' }</td>
                                                                                        <td style={{
                                                                                            color: item.textColor, maxWidth: "240px",
                                                                                            whiteSpace: "nowrap",
                                                                                            overflow: "hidden",
                                                                                            textOverflow: "ellipsis"
                                                                                        }}>{ (item.skuName && item.skuName !== 'Invalid ID') ? item.skuName : '-' }</td>
                                                                                        <td style={{ color: item.textColor, minWidth: "126px" }}>
                                                                                            {isFailed || isPending || !hasStatus ? '-' : `SGD ${formatPrice(item.platformPrice)}`}
                                                                                        </td>
                                                                                        <td style={{ color: item.textColor }}>
                                                                                            {isFailed || isPending || !hasStatus ? '-' : item.platformStock}
                                                                                        </td>
                                                                                        <td style={{ color: item.textColor }}>
                                                                                            {isPending || !hasStatus ? '-' : item.lastSyncTime}
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            // Show "-" when no platform data
                                                                            <tr>
                                                                                <td colSpan="6" className="py-3 text-center" style={{ color: '#6C757D' }}>-</td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {drawerTab === 'additional-info' && (
                                                    <div
                                                        className="card-section"
                                                        style={{
                                                            borderRadius: '12px',
                                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                            overflow: 'hidden',
                                                            marginTop: '40px',
                                                        }}
                                                    >
                                                        <div
                                                            className="card-header"
                                                            style={{
                                                                backgroundColor: '#F8F9FA',
                                                                padding: '10px 16px',
                                                                fontFamily: 'Montserrat',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                color: '#212529',
                                                                borderBottom: 'none',
                                                            }}
                                                        >
                                                            Notes
                                                        </div>

                                                        <div
                                                            className="card-body"
                                                            style={{
                                                                backgroundColor: '#FFFFFF',
                                                                padding: '16px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '12px',
                                                                wordBreak: 'break-word',
                                                                overflowWrap: 'break-word',
                                                                whiteSpace: 'normal',
                                                            }}
                                                        >
                                                            <div className="field-block" style={{ padding: "0px" }}>
                                                                <div
                                                                    className="field-value"
                                                                    style={{
                                                                        background: '#FFFFFF',
                                                                        fontSize: '13px',
                                                                        color: selectedRow.notes ? '#212529' : '#212529',
                                                                        minHeight: '24px',
                                                                        whiteSpace: 'pre-line',
                                                                    }}
                                                                >
                                                                    {selectedRow.notes || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    </div >
                                </>
                            );

                        })()}
                        {deactivateModalOpen && (
                            <ConfirmDeactivateModal
                                isOpen={deactivateModalOpen}
                                onCancel={() => setDeactivateModalOpen(false)}
                                onConfirm={() => {
                                    handleDeactivateStock(selectedListing.id);
                                }}
                                entityName="Listing"
                            />
                        )}
                        {archivedModalOpen && (
                            <ConfirmArchiveModal
                                isOpen={archivedModalOpen}
                                onCancel={() => {
                                    setArchivedModalOpen(false);
                                    setSelectedStockId(null);
                                }}
                                onConfirm={() => handleArchiveStock(selectedStockId)}
                                entityName="Listing"
                            />
                        )}
                    </div>
                )}

                {activeTab === 'archived-listing' && (
                    <div className="tab-content" id="archived-listing-content">
                        {/* Stock List Card */}
                        <div className="info-section">
                            <div className="info-card">
                                <div className="entities-tab">
                                    <div className="toolbar">
                                        <div className="toolbar-left">
                                            <SearchBox
                                                searchInput={searchInputArchived}
                                                setSearchInput={setSearchInputArchived}
                                                setSearchTerm={setSearchTermArchived}
                                                setCurrentPage={setCurrentPageArchived}
                                                disabled={isArchivedTableEmpty}
                                            />
                                        </div>
                                    </div>

                                    <div className="table-wrapper" style={{ border: "none" }}>
                                        {
                                            filteredArchived.length === 0 && !searchTermArchived ? (
                                                <div className="empty-state">
                                                    <div className="empty-state-content">
                                                        <div className="icon"><img
                                                            src={noBrandImage}
                                                            alt="avatar"
                                                            className="avatar"
                                                            style={{
                                                                width: 120,
                                                                height: 120,
                                                                verticalAlign: 'middle'
                                                            }}
                                                        /></div>
                                                        <h1 className="no-item-title mt-2">
                                                            No Archived Listings Found
                                                        </h1>
                                                        <p className="no-item-text">
                                                            Nothing here yet. Add your first item to get started!
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : filteredArchived.length === 0 && searchTermArchived ? (
                                                <div className="empty-state">
                                                    <div className="empty-state-content">
                                                        <div className="icon"><img
                                                            src={emptySearchImg}
                                                            alt="avatar"
                                                            className="avatar"
                                                            style={{
                                                                width: 120,
                                                                height: 120,
                                                                verticalAlign: 'middle'
                                                            }}
                                                        /></div>
                                                        <h1 className="no-item-title mt-2">
                                                            No Archived Listings Found
                                                        </h1>
                                                        <p className="no-item-text">
                                                            No results match what you're looking for
                                                        </p>
                                                    </div>
                                                </div>

                                            ) : (
                                                <table className="entities-table">
                                                    <thead height="45px">
                                                        <tr>
                                                            <SortableHeader
                                                                label="Stock"
                                                                field="name"
                                                                sortField={sortField}
                                                                sortOrder={sortOrder}
                                                                handleSort={handleSort}
                                                            />

                                                            <SortableHeader
                                                                label="ID"
                                                                field="iD"
                                                                sortField={sortField}
                                                                sortOrder={sortOrder}
                                                                handleSort={handleSort}
                                                            />
                                                            <SortableHeader
                                                                label="Last Modified Time"
                                                                field="modified_at"
                                                                sortField={sortField}
                                                                sortOrder={sortOrder}
                                                                handleSort={handleSort}
                                                            />
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {pageDataArchived.map((row, idx) => {
                                                            const isRowSelected = (selectedIdsArchived.includes(row.id) || dropdownOpenIdArchived === row.id) && !suppressHighlightArchived;

                                                            return (
                                                                <tr
                                                                    key={row.id}
                                                                    className={[idx % 2 ? "striped" : "", isRowSelected ? "row-selected" : ""].join(" ")}
                                                                    onClick={() => onRowClickArchived(row)}
                                                                >

                                                                    <td className="sticky-action-col first">
                                                                        <div className="name-cell" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                                            <img src={row.avatar} alt="avatar" className="avatar" style={{ width: 40, height: 40, marginRight: 8, verticalAlign: "middle" }} />
                                                                            <div>
                                                                                <div>{row.name}</div>
                                                                                <div className="sub-line mt-1">{row.variant}{row.model_number ? ` | ${row.model_number}` : ''}</div>
                                                                                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5 }}>
                                                                                    <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00" }}>{row.condition}</span>
                                                                                    <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00" }}>{row.condition_type}</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </td>

                                                                    {/* ID - Show NA in #212529 */}
                                                                    <td>
                                                                        {row.iD ? row.iD : <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </td>
                                                                    <td>
                                                                        <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
                                                                            {formatDateTime(row.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )
                                        }
                                    </div>

                                    {/* Archive pagination */}
                                    <div className="pagination" style={{ marginTop: 16 }}>
                                        <div className="pagination-info">
                                            {`${(currentPageArchived - 1) * PAGE_SIZE + 1} - ${Math.min(currentPageArchived * PAGE_SIZE, filteredArchived.length)} of ${filteredArchived.length} `}
                                        </div>
                                        <Pagination
                                            currentPage={currentPageArchived}
                                            totalPages={totalPagesArchived}
                                            onPageChange={setCurrentPageArchived}
                                        />
                                    </div>

                                    {/* ------------------ End Archive Filter Drawer ------------------ */}

                                    {saveModalOpen && (
                                        <>
                                            <div className="drawer-overlay" />
                                            <div
                                                className="entity-drawer modal-popup-cls"
                                                ref={saveRef}
                                                style={{
                                                    maxWidth: 650,
                                                    margin: "0 auto",
                                                    background: "white",
                                                    borderRadius: 12,
                                                    zIndex: 1050,
                                                    padding: 24,
                                                    position: "fixed",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                                    height: "auto",
                                                    right: "auto",
                                                    overflow: "inherit",
                                                }}
                                            >
                                                <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                                    <button className="close-btn" onClick={() => setSaveModalOpen(false)}>
                                                        <X size={20} />
                                                    </button>
                                                </div>
                                                <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>New feature in Progress</h3>
                                                <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>Work in Progress</p>
                                                <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                                    <button className="btn-save" onClick={handleSaveConfirm}>
                                                        OK
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Single‐row Drawer */}
                        {drawerOpenArchived && selectedRowArchived && (() => {
                            const pendingCount = selectedRowArchived.pending?.reduce((sum, item) => sum + item.count, 0) || 0;
                            const total = Number(selectedRowArchived.available || 0) + pendingCount;
                            return (
                                <>
                                    <div className="drawer-overlay" />
                                    <div className="entity-drawer" ref={drawerRefArchived}>
                                        <div className="drawer-header" style={{ background: "#F8F9FA" }}>
                                            <h3>Listing Info</h3>
                                            <button
                                                className="close-btn"
                                                onClick={() => {
                                                    setDrawerOpenArchived(false);
                                                    setSelectedIdsArchived(ids => ids.filter(id => id !== selectedRowArchived?.id));
                                                    setSelectedRowArchived(null);
                                                }}
                                            >
                                                <X size={24} />
                                            </button>
                                        </div>

                                        <div className="main-drawer-inner" >
                                            <div className="drawer-title d-flex align-items-center justify-content-between" style={{ borderBottom: "none", paddingBottom: "0" }}>
                                                <div className="name-cell" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <img
                                                        src={selectedRowArchived.avatar}
                                                        alt="avatar"
                                                        className="avatar"
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            marginRight: 8,
                                                            verticalAlign: "middle"
                                                        }}
                                                    />
                                                    <div>
                                                        <div style={{ color: "#212529", fontStyle: "normal", fontWeight: "400", lineHeight: "normal", fontSize: '12px' }}>{selectedRowArchived.name}</div>
                                                        <div style={{ color: "#6C757D", fontSize: "10px", fontStyle: "normal", fontWeight: "400", lineHeight: "normal" }} className="sub-line mt-1">{selectedRowArchived.variant}{selectedRowArchived.model_number ? ` | ${selectedRowArchived.model_number}` : ''}</div>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5 }}>
                                                            <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00", borderRadius: "99px", color: "#212529", fontWeight: "600" }}>{selectedRowArchived.condition}</span>
                                                            <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00", borderRadius: "99px", color: "#212529", fontWeight: "600" }}>{selectedRowArchived.grading}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="drawer-tabs">
                                                <button
                                                    className={drawerTabArchived === 'entity-info' ? 'active' : ''}
                                                    onClick={() => setDrawerTabArchived('entity-info')}
                                                >
                                                    General
                                                </button>

                                                <button
                                                    className={drawerTabArchived === 'additional-info' ? 'active' : ''}
                                                    onClick={() => setDrawerTabArchived('additional-info')}
                                                >
                                                    Additional
                                                </button>
                                            </div>

                                            <div className="drawer-content">
                                                {drawerTabArchived === 'entity-info' && (
                                                    <div className="tab-panel general-panel">
                                                        <div
                                                            className="card-section"
                                                            style={{
                                                                borderRadius: '12px',
                                                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                                overflow: 'hidden',
                                                                marginBottom: '20px',
                                                                marginTop: '15px',
                                                            }}
                                                        >
                                                            <div
                                                                className="card-header"
                                                                style={{
                                                                    backgroundColor: ' #F8F9FA',
                                                                    padding: '15px 20px',
                                                                    fontFamily: 'Montserrat',
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    color: ' #212529',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    alignSelf: 'stretch',
                                                                    borderRadius: '12px 12px 0px 0px',
                                                                    gap: '10px',
                                                                    height: '50px',
                                                                    borderBottom: 'none',
                                                                }}
                                                            >
                                                                Basic Info
                                                            </div>
                                                            <div
                                                                className="card-body"
                                                                style={{
                                                                    backgroundColor: '#FFFFFF',
                                                                    padding: '16px',
                                                                    display: 'grid',
                                                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                                                    gap: '16px',
                                                                }}
                                                            >
                                                                <div className="field-block" style={{
                                                                    borderBottom: 'none',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    minHeight: '60px',
                                                                    padding: '0'
                                                                }} >
                                                                    <div className="field-label">Listing Stock</div>
                                                                    <div className="name-cell" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                                        <img
                                                                            src={selectedRowArchived.avatar}
                                                                            alt="avatar"
                                                                            className="avatar"
                                                                            style={{
                                                                                width: 32,
                                                                                height: 32,
                                                                                marginRight: 8,
                                                                                verticalAlign: "middle"
                                                                            }}
                                                                        />
                                                                        <div>
                                                                            <div style={{ color: "#212529", fontStyle: "normal", fontWeight: "500", lineHeight: "normal", fontSize: '12px' }}>{selectedRowArchived.name}</div>
                                                                            <div style={{ color: "#6C757D", fontSize: "10px", fontStyle: "normal", fontWeight: "400", lineHeight: "normal" }} className="sub-line mt-1">{selectedRowArchived.variant}{selectedRowArchived.model_number ? ` | ${selectedRowArchived.model_number}` : ''}</div>
                                                                            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5 }}>
                                                                                <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00", borderRadius: "99px", color: "#212529", fontWeight: "600" }}>{selectedRowArchived.condition}</span>
                                                                                <span className="status" style={{ minWidth: "unset", padding: "2px 8px", fontSize: "8px", lineHeight: "normal", background: "#FFEB00", borderRadius: "99px", color: "#212529", fontWeight: "600" }}>{selectedRowArchived.grading}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* <div className="field-block" style={{
                                                                    borderBottom: 'none',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    minHeight: '60px',
                                                                    padding: '0'
                                                                }}>
                                                                    <div className="field-label">Status</div>
                                                                    <div className="field-value">
                                                                        <span className={`status ${String(selectedRowArchived?.status || '').toLowerCase()}`}>
                                                                            {selectedRowArchived?.status || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </span>
                                                                    </div>
                                                                </div> */}
                                                                <div
                                                                    className="field-block"
                                                                    style={{
                                                                        borderBottom: 'none',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'flex-start',
                                                                        padding: '0'
                                                                    }}>
                                                                    <div className="field-label" style={{ marginBottom: '4px' }}>ID</div>
                                                                    <div className="field-value" style={{ width: '100%', wordBreak: 'break-all' }}>
                                                                        {selectedRowArchived.iD || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="field-block"
                                                                    style={{
                                                                        borderBottom: 'none',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'flex-start',
                                                                        padding: '0',
                                                                        gridColumn: '1 / -1',
                                                                        width: '100%',
                                                                    }}
                                                                >
                                                                    <div className="field-label" style={{ marginBottom: '4px' }}>
                                                                        Open ID
                                                                    </div>

                                                                    <div
                                                                        className="field-value"
                                                                        style={{ width: '100%', wordBreak: 'break-all', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                                    >
                                                                        {selectedRowArchived?.openId ? (
                                                                            <div className="field-value d-flex align-items-center gap-2">
                                                                                <div>
                                                                                <span
                                                                                    style={{
                                                                                        color: '#0D6EFD',
                                                                                        cursor: 'pointer',
                                                                                        fontWeight: '500',
                                                                                        textDecoration: 'underline',
                                                                                    }}
                                                                                    onClick={() => handleCopy(selectedRowArchived?.openId)}
                                                                                >
                                                                                    {selectedRowArchived?.openId}
                                                                                    </span>
                                                                                    <Copy
                                                                                        size={13}
                                                                                        onClick={() => handleCopy(selectedRowArchived?.openId)}
                                                                                        style={{ cursor: 'pointer', color: '#0D6EFD', marginLeft: '6px', }}
                                                                                        />
                                                                                        {copied && (
                                                                                            <span style={{ fontSize: '12px', color: '#0D6EFD',textDecoration: 'none',marginLeft: '6px',lineHeight:'normal',fontWeight: 500 }}>
                                                                                                Copied!
                                                                                            </span>
                                                                                        )}
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <span style={{ color: '#ADB5BD' }}>NA</span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0' }}>
                                                                    <div className="field-label">Created By</div>
                                                                    <div className="field-value">{selectedRowArchived?.created_by || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                                </div>

                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', }}>
                                                                    <div className="field-label">Created Time</div>
                                                                    <div className="field-value">
                                                                        {formatDateTime(selectedRowArchived?.created_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>

                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', }}>
                                                                    <div className="field-label">Last Modified By</div>
                                                                    <div className="field-value">
                                                                        {selectedRowArchived?.modified_by || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>

                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', }}>
                                                                    <div className="field-label">Last Modified Time</div>
                                                                    <div className="field-value">
                                                                        {formatDateTime(selectedRowArchived?.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>
                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', }}>
                                                                    <div className="field-label">Archived By</div>
                                                                    <div className="field-value">
                                                                        {selectedRowArchived?.archived_by || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>

                                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', }}>
                                                                    <div className="field-label">Archived Time</div>
                                                                    <div className="field-value">
                                                                        {formatDateTime(selectedRowArchived?.archived_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {stockDrawerId && (
                                                            <StockDrawer
                                                                stockId={stockDrawerId}
                                                                setDrawerOpen={() => setStockDrawerId(null)}
                                                                setSkuDrawerOpen={setSkuDrawerOpen}
                                                                setSelectedRow={setSelectedSkuRow}
                                                                fallbackModelNumber={stockDrawerModelNumber}
                                                                skuDrawerOpen={skuDrawerOpen}
                                                            />
                                                        )}
                                                        {skuDrawerOpen && (
                                                            <SkuInfoDrawer
                                                                selectedRow={selectedSkuRow}
                                                                setSelectedRow={setSelectedSkuRow}
                                                                setSkuDrawerOpen={setSkuDrawerOpen}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                                {drawerTabArchived === 'additional-info' && (
                                                    <div
                                                        className="card-section"
                                                        style={{
                                                            borderRadius: '12px',
                                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                                                            overflow: 'hidden',
                                                            marginTop: '40px',
                                                        }}
                                                    >
                                                        <div
                                                            className="card-header"
                                                            style={{
                                                                backgroundColor: '#F8F9FA',
                                                                padding: '10px 16px',
                                                                fontFamily: 'Montserrat',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                color: '#212529',
                                                                borderBottom: 'none',
                                                            }}
                                                        >
                                                            Notes
                                                        </div>

                                                        <div
                                                            className="card-body"
                                                            style={{
                                                                backgroundColor: '#FFFFFF',
                                                                padding: '16px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '12px',
                                                                wordBreak: 'break-word',
                                                                overflowWrap: 'break-word',
                                                                whiteSpace: 'normal',
                                                            }}
                                                        >
                                                            <div className="field-block" style={{ padding: "0px" }}>
                                                                <div
                                                                    className="field-value"
                                                                    style={{
                                                                        background: '#FFFFFF',
                                                                        fontSize: '13px',
                                                                        color: selectedRowArchived.notes ? '#212529' : '#ADB5BD',
                                                                        minHeight: '24px',
                                                                        whiteSpace: 'pre-line',
                                                                    }}
                                                                >
                                                                    {selectedRowArchived.notes || 'NA'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                </>
                            );
                        })()}

                        {archivedModalOpen && (
                            <>
                                <div className="drawer-overlay" />
                                <div className="entity-drawer modal-popup-cls" ref={archivedRef}
                                    style={{
                                        maxWidth: 650,
                                        margin: "0 auto",
                                        background: "white",
                                        borderRadius: 12,
                                        zIndex: 1050,
                                        padding: 24,
                                        position: "fixed",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                        height: "auto",
                                        right: "auto",
                                        overflow: "inherit",
                                    }}>
                                    <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                        <button className="close-btn" onClick={() => setArchivedModalOpen(false)}>
                                            <X size={20} />
                                        </button>
                                    </div>

                                    <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>Branch can’t be archived</h3>
                                    <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>
                                        Branch stocked with items can’t be archived, clear stock before archiving. Archive action is also subject to approval
                                    </p>

                                    <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                        <button className="btn-save" onClick={handleArchivedConfirm}>
                                            OK
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}